import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash, Tag, Warning, Handshake, ArrowsLeftRight } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { InlineEdit } from '@/components/DraggableList';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';
import { useConfirmDelete } from '@/components/ConfirmDialogs';

const API = process.env.REACT_APP_BACKEND_URL;

function LocationsEditor() {
  const confirmDelete = useConfirmDelete();
  const [locations, setLocations] = useState([]);
  const [newLoc, setNewLoc] = useState('');
  const fetch = useCallback(async () => { try { const { data } = await axios.get(`${API}/api/config/consultation-locations`); setLocations(data); } catch {} }, []);
  useEffect(() => { fetch(); }, [fetch]);
  const save = async (locs) => { await axios.put(`${API}/api/config/consultation-locations`, { locations: locs }); setLocations(locs); };
  const add = () => { if (!newLoc.trim()) return; save([...locations, newLoc.trim()]); setNewLoc(''); toast.success('Local adicionado'); };
  const remove = async (idx) => {
    const ok = await confirmDelete({ description: `Vai apagar o local de consulta "${locations[idx]}".` });
    if (!ok) return;
    save(locations.filter((_, i) => i !== idx));
  };
  return (
    <div className="mt-8 pt-5 border-t border-brand-border" data-testid="locations-editor">
      <h3 className="font-heading font-medium text-sm text-brand-text mb-3">Locais de Consulta</h3>
      <p className="text-xs text-brand-muted mb-3">Gerir locais disponíveis no campo "Local de consulta" da ficha de paciente.</p>
      <div className="space-y-1.5 mb-3">
        {locations.map((l, i) => (
          <div key={i} className="flex items-center justify-between bg-white border border-brand-border rounded-xl px-3 py-2">
            <span className="text-sm text-brand-text">{l}</span>
            <button onClick={() => remove(i)} className="text-brand-placeholder hover:text-red-500"><Trash size={13} /></button>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={newLoc} onChange={e => setNewLoc(e.target.value)} placeholder="Novo local..." className="flex-1 rounded-xl border-brand-border" onKeyDown={e => { if (e.key === 'Enter') add(); }} data-testid="new-location-input" />
        <button onClick={add} className="bg-brand-green text-white rounded-full px-4 py-2 text-sm font-medium" data-testid="add-location-btn"><Plus size={14} /></button>
      </div>
    </div>
  );
}

export default function PatientCategoriesPage() {
  useRequireAdmin();
  const confirmDelete = useConfirmDelete();
  const [tags, setTags] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newTag, setNewTag] = useState({ value: '', label: '' });
  const [showMerge, setShowMerge] = useState(false);
  const [mergeForm, setMergeForm] = useState({ keep: '', remove: '' });
  const [merging, setMerging] = useState(false);

  const fetch = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/patient-tags/with-counts`); setTags(data); } catch {}
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const save = async (updatedTags) => {
    try { await axios.put(`${API}/api/patient-tags`, { tags: updatedTags.map(({ patient_count, ...t }) => t) }); fetch(); } catch { toast.error('Erro ao guardar'); }
  };

  const addTag = () => {
    if (!newTag.value.trim() || !newTag.label.trim()) { toast.error('Preenche a chave e o nome'); return; }
    const val = newTag.value.trim().toLowerCase().replace(/\s+/g, '_');
    if (tags.some(t => t.value === val)) { toast.error('Esta chave já existe'); return; }
    const updated = [...tags, { value: val, label: newTag.label.trim(), exclude_from_metrics: false, is_partnership: false }];
    save(updated);
    setNewTag({ value: '', label: '' }); setShowAdd(false);
    toast.success('Categoria criada');
  };

  const renameTag = (idx, label) => { const u = [...tags]; u[idx] = { ...u[idx], label }; save(u); };
  const toggleExclude = (idx) => { const u = [...tags]; u[idx] = { ...u[idx], exclude_from_metrics: !u[idx].exclude_from_metrics }; save(u); };
  const togglePartnership = (idx) => {
    const u = [...tags];
    const newVal = !u[idx].is_partnership;
    u[idx] = { ...u[idx], is_partnership: newVal };
    if (!newVal) { u[idx].partner_name = ''; u[idx].partner_percentage = 0; u[idx].payment_handler = ''; }
    save(u);
  };
  const updatePartnerField = (idx, field, value) => { const u = [...tags]; u[idx] = { ...u[idx], [field]: value }; save(u); };
  const deleteTag = async (val) => {
    const t = tags.find(x => x.value === val);
    const ok = await confirmDelete({ description: `Vai apagar a categoria "${t?.label || val}".` });
    if (!ok) return;
    try { await axios.delete(`${API}/api/patient-tags/${val}`); toast.success('Categoria eliminada'); fetch(); } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  const doMerge = async () => {
    if (!mergeForm.keep || !mergeForm.remove || mergeForm.keep === mergeForm.remove) { toast.error('Escolhe duas tags diferentes'); return; }
    setMerging(true);
    try {
      const { data } = await axios.post(`${API}/api/patient-tags/merge`, mergeForm);
      toast.success(data.message || 'Tags fundidas');
      setShowMerge(false); setMergeForm({ keep: '', remove: '' }); fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao fundir'); }
    finally { setMerging(false); }
  };

  const directTags = tags.filter(t => !t.is_partnership);
  const partnerTags = tags.filter(t => t.is_partnership);

  return (
    <div className="fade-in" data-testid="patient-categories-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-heading font-medium text-xl text-brand-text">Categorias de Pacientes</h2>
          <p className="text-xs text-brand-muted mt-0.5">Categorias directas e parcerias</p>
        </div>
        <button onClick={() => { setNewTag({ value: '', label: '' }); setShowAdd(true); }} className="bg-brand-green text-white rounded-full px-4 py-2.5 text-sm font-medium flex items-center gap-2" data-testid="add-category-btn">
          <Plus size={16} /> Nova categoria
        </button>
      </div>

      <button onClick={() => { setMergeForm({ keep: '', remove: '' }); setShowMerge(true); }} className="mb-5 flex items-center gap-2 text-xs font-medium text-brand-muted border border-brand-border rounded-full px-3.5 py-2 hover:border-brand-green hover:text-brand-green" data-testid="merge-tags-btn">
        <ArrowsLeftRight size={14} /> Fundir tags duplicadas
      </button>

      {/* Direct categories */}
      <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-3">Programas directos</h3>
      <div className="space-y-2 mb-6">
        {directTags.map((t) => {
          const idx = tags.indexOf(t);
          return (
            <div key={t.value} className={`bg-white border rounded-2xl p-4 ${t.exclude_from_metrics ? 'border-amber-200 bg-amber-50/30' : 'border-brand-border'}`} data-testid={`category-${t.value}`}>
              <div className="flex items-center gap-4">
                <div className="w-9 h-9 rounded-xl bg-brand-mint/20 flex items-center justify-center flex-shrink-0"><Tag size={16} className="text-brand-green" /></div>
                <div className="flex-1 min-w-0">
                  <InlineEdit value={t.label} onSave={v => renameTag(idx, v)} className="text-sm font-medium text-brand-text" testId={`cat-name-${t.value}`} />
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] text-brand-muted font-mono bg-brand-bg px-1.5 py-0.5 rounded">{t.value}</span>
                    <span className="text-[10px] text-brand-muted">{t.patient_count || 0} paciente{(t.patient_count || 0) !== 1 ? 's' : ''}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <label className="flex items-center gap-1.5 cursor-pointer"><input type="checkbox" checked={!!t.is_partnership} onChange={() => togglePartnership(idx)} className="rounded border-brand-border w-4 h-4" /><span className="text-[10px] text-brand-muted">Parceria</span></label>
                  <label className="flex items-center gap-1.5 cursor-pointer"><input type="checkbox" checked={!!t.exclude_from_metrics} onChange={() => toggleExclude(idx)} className="rounded border-brand-border w-4 h-4" /><span className="text-[10px] text-brand-muted">Excluir métricas</span></label>
                  {(t.patient_count || 0) === 0 ? <button onClick={() => deleteTag(t.value)} className="p-1.5 text-brand-placeholder hover:text-red-500"><Trash size={14} /></button> : <span className="text-[9px] text-brand-placeholder">Em uso</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Partnerships */}
      {partnerTags.length > 0 && (
        <>
          <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-3 flex items-center gap-1.5"><Handshake size={14} /> Parcerias</h3>
          <div className="space-y-2 mb-6">
            {partnerTags.map((t) => {
              const idx = tags.indexOf(t);
              return (
                <div key={t.value} className="bg-white border border-purple-200 rounded-2xl p-4" data-testid={`partnership-${t.value}`}>
                  <div className="flex items-center gap-4 mb-3">
                    <div className="w-9 h-9 rounded-xl bg-purple-50 flex items-center justify-center flex-shrink-0"><Handshake size={16} className="text-purple-600" /></div>
                    <div className="flex-1 min-w-0">
                      <InlineEdit value={t.label} onSave={v => renameTag(idx, v)} className="text-sm font-medium text-brand-text" testId={`cat-name-${t.value}`} />
                      <span className="text-[10px] text-brand-muted">{t.patient_count || 0} paciente{(t.patient_count || 0) !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <label className="flex items-center gap-1.5 cursor-pointer"><input type="checkbox" checked={!!t.is_partnership} onChange={() => togglePartnership(idx)} className="rounded border-brand-border w-4 h-4" /><span className="text-[10px] text-purple-600">Parceria</span></label>
                      {(t.patient_count || 0) === 0 ? <button onClick={() => deleteTag(t.value)} className="p-1.5 text-brand-placeholder hover:text-red-500"><Trash size={14} /></button> : <span className="text-[9px] text-brand-placeholder">Em uso</span>}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3 bg-purple-50/50 rounded-xl p-3">
                    <div>
                      <Label className="text-[10px] text-brand-muted mb-0.5 block">Nome do parceiro</Label>
                      <Input value={t.partner_name || ''} onBlur={e => updatePartnerField(idx, 'partner_name', e.target.value)} onChange={e => { const u = [...tags]; u[idx] = { ...u[idx], partner_name: e.target.value }; setTags(u); }} placeholder="Ex: Clínica XYZ" className="rounded-lg border-brand-border text-xs h-8" />
                    </div>
                    <div>
                      <Label className="text-[10px] text-brand-muted mb-0.5 block">% Nutricionista</Label>
                      <Input type="number" value={t.partner_percentage || ''} onBlur={e => updatePartnerField(idx, 'partner_percentage', parseFloat(e.target.value) || 0)} onChange={e => { const u = [...tags]; u[idx] = { ...u[idx], partner_percentage: e.target.value }; setTags(u); }} placeholder="70" className="rounded-lg border-brand-border text-xs h-8" />
                    </div>
                    <div>
                      <Label className="text-[10px] text-brand-muted mb-0.5 block">Pagamento gerido por</Label>
                      <Input value={t.payment_handler || ''} onBlur={e => updatePartnerField(idx, 'payment_handler', e.target.value)} onChange={e => { const u = [...tags]; u[idx] = { ...u[idx], payment_handler: e.target.value }; setTags(u); }} placeholder="Ex: equipa parceiro" className="rounded-lg border-brand-border text-xs h-8" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Info box */}
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl flex gap-3">
        <Warning size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-amber-800 leading-relaxed">
          <p className="font-medium mb-1">Como funcionam as parcerias</p>
          <p>Categorias marcadas como "Parceria" aparecem numa secção separada no CEO Dashboard com contagem de consultas e estimativa de receita. Não misturam com as métricas de clientes directas.</p>
        </div>
      </div>

      {/* Locais de consulta */}
      <LocationsEditor />

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Nova categoria</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-brand-muted mb-1 block">Nome visível</Label><Input value={newTag.label} onChange={e => setNewTag({ ...newTag, label: e.target.value, value: e.target.value.toLowerCase().replace(/\s+/g, '_').normalize('NFD').replace(/[\u0300-\u036f]/g, '') })} placeholder="Ex: Pediatria" className="rounded-xl border-brand-border" data-testid="new-cat-label" /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Chave interna</Label><Input value={newTag.value} onChange={e => setNewTag({ ...newTag, value: e.target.value })} placeholder="Ex: pediatria" className="rounded-xl border-brand-border font-mono text-xs" data-testid="new-cat-value" /></div>
            <button onClick={addTag} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="confirm-add-cat">Criar categoria</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showMerge} onOpenChange={setShowMerge}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Fundir tags duplicadas</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-brand-muted">Escolhe a tag que fica e a que desaparece. Todas as pacientes e os Grupos de foco diários passam para a tag que fica, a outra é removida da lista. Esta ação não pode ser desfeita.</p>
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Tag que fica</Label>
              <select value={mergeForm.keep} onChange={e => setMergeForm({ ...mergeForm, keep: e.target.value })} className="w-full rounded-xl border border-brand-border text-sm px-3 py-2" data-testid="merge-keep-select">
                <option value="">Escolhe...</option>
                {tags.map(t => <option key={t.value} value={t.value}>{t.label} ({t.value})</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Tag que desaparece</Label>
              <select value={mergeForm.remove} onChange={e => setMergeForm({ ...mergeForm, remove: e.target.value })} className="w-full rounded-xl border border-brand-border text-sm px-3 py-2" data-testid="merge-remove-select">
                <option value="">Escolhe...</option>
                {tags.filter(t => t.value !== mergeForm.keep).map(t => <option key={t.value} value={t.value}>{t.label} ({t.value})</option>)}
              </select>
            </div>
            <button onClick={doMerge} disabled={merging} className="w-full bg-brand-green text-white rounded-full py-3 font-medium disabled:opacity-50" data-testid="confirm-merge-btn">{merging ? 'A fundir...' : 'Fundir tags'}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
